import type { ScheduleEntry, ScheduleOverride } from "../lib/realestate";

export interface DraftRow {
  month: string;
  interestCents: number | null;
  principalCents: number | null;
  notes: string | null;
  existingOverrideId: string | null;
}

export type DiffAction = "NOOP" | "UPSERT" | "DELETE";

export type ValidationResult = "OK" | "NEGATIVE";

function entryYearMonth(entry: ScheduleEntry): string {
  return entry.date.slice(0, 7);
}

function monthAsIso(yearMonth: string): string {
  return `${yearMonth}-01`;
}

export function initDraftRow(entry: ScheduleEntry, override: ScheduleOverride | null): DraftRow {
  if (override) {
    return {
      month: override.month,
      interestCents: override.interestCents,
      principalCents: override.principalCents,
      notes: override.notes,
      existingOverrideId: override.id,
    };
  }
  return {
    month: monthAsIso(entryYearMonth(entry)),
    interestCents: null,
    principalCents: null,
    notes: null,
    existingOverrideId: null,
  };
}

export function initDraftRowsMap(
  schedule: ScheduleEntry[],
  overrides: ScheduleOverride[],
): Map<string, DraftRow> {
  const map = new Map<string, DraftRow>();
  for (const entry of schedule) {
    const ym = entryYearMonth(entry);
    const ov = overrides.find((o) => o.month.slice(0, 7) === ym) ?? null;
    map.set(ym, initDraftRow(entry, ov));
  }
  return map;
}

export function computeAction(
  draft: DraftRow,
  entry: ScheduleEntry,
  existing: ScheduleOverride | null,
): DiffAction {
  const bothNull = draft.interestCents === null && draft.principalCents === null;

  if (bothNull) {
    return existing ? "DELETE" : "NOOP";
  }

  if (existing) {
    const sameInterest = draft.interestCents === existing.interestCents;
    const samePrincipal = draft.principalCents === existing.principalCents;
    const sameNotes = (draft.notes ?? null) === (existing.notes ?? null);
    return sameInterest && samePrincipal && sameNotes ? "NOOP" : "UPSERT";
  }

  const nonNullFieldsMatchComputed =
    (draft.interestCents === null || draft.interestCents === entry.interestCents) &&
    (draft.principalCents === null || draft.principalCents === entry.principalCents);

  return nonNullFieldsMatchComputed ? "NOOP" : "UPSERT";
}

export function validateDraftRow(draft: DraftRow): ValidationResult {
  if (draft.interestCents !== null && draft.interestCents < 0) return "NEGATIVE";
  if (draft.principalCents !== null && draft.principalCents < 0) return "NEGATIVE";
  return "OK";
}
